import Cropper, { type Area } from "react-easy-crop";

import { buildCssFilter, getAspectRatioValue } from "../../lib/image-processing";
import type { EditorState, RenderedImage, UploadedImage } from "../../types/image";
import { Badge } from "../ui/Badge";

interface WorkspaceStageProps {
  image: UploadedImage;
  editorState: EditorState;
  renderedImage: RenderedImage | null;
  renderStatus: "idle" | "loading" | "success" | "error";
  renderError: string | null;
  onCropChange: (crop: EditorState["crop"]) => void;
  onCropComplete: (croppedArea: Area, croppedAreaPixels: Area) => void;
  onZoomChange: (zoom: number) => void;
}

export function WorkspaceStage({
  image,
  editorState,
  renderedImage,
  renderStatus,
  renderError,
  onCropChange,
  onCropComplete,
  onZoomChange
}: WorkspaceStageProps) {
  const compareBefore = editorState.compareMode === "before";

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-[28px] border border-white/8 bg-[#050304]">
        <div className="absolute left-4 top-4 z-20">
          <Badge className="border-accent/20 bg-black/50 text-white">{compareBefore ? "Before" : "Edited workspace"}</Badge>
        </div>
        <div className="relative min-h-[420px] bg-[radial-gradient(circle_at_top,rgba(255,68,104,0.08),transparent_28%),linear-gradient(180deg,#120b0e_0%,#040304_100%)] sm:min-h-[520px]">
          {compareBefore ? (
            <img alt="Original upload preview" className="h-full max-h-[640px] w-full object-contain p-6" src={image.objectUrl} />
          ) : (
            <div className="absolute inset-0">
              <Cropper
                aspect={getAspectRatioValue(editorState.aspectPreset)}
                crop={editorState.crop}
                cropShape="rect"
                image={image.objectUrl}
                objectFit="contain"
                rotation={editorState.rotation}
                showGrid
                style={{
                  cropAreaStyle: {
                    border: "1px solid rgba(255,255,255,0.7)",
                    boxShadow: "0 0 0 9999em rgba(0,0,0,0.52)"
                  },
                  mediaStyle: {
                    filter: buildCssFilter(editorState)
                  }
                }}
                transform={`translate(${editorState.crop.x}px, ${editorState.crop.y}px) rotate(${editorState.rotation}deg) scale(${editorState.zoom}) scaleX(${editorState.flipHorizontal ? -1 : 1}) scaleY(${editorState.flipVertical ? -1 : 1})`}
                zoom={editorState.zoom}
                onCropChange={onCropChange}
                onCropComplete={onCropComplete}
                onZoomChange={onZoomChange}
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <PreviewTile label="Original" src={image.objectUrl} />
        <PreviewTile
          label={renderStatus === "error" ? "Render error" : "Edited candidate"}
          message={renderError ?? (renderStatus === "loading" ? "Rendering preview..." : undefined)}
          src={renderedImage?.objectUrl ?? image.objectUrl}
        />
      </div>
    </div>
  );
}

function PreviewTile({ label, message, src }: { label: string; message?: string; src: string }) {
  return (
    <div className="overflow-hidden rounded-[24px] border border-white/8 bg-white/[0.04]">
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-xs uppercase tracking-[0.18em] text-ink-muted">{label}</p>
        {message ? <p className="text-xs text-accent-strong">{message}</p> : null}
      </div>
      <img alt={label} className="h-44 w-full object-contain bg-black/20 p-3" src={src} />
    </div>
  );
}
